/** @prettier */

import {
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Stack,
} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {
  useSensor,
  useSensors,
  DragEndEvent,
  closestCenter,
  DndContext,
  KeyboardSensor,
  DragOverlay,
  UniqueIdentifier,
  DragStartEvent,
  TouchSensor,
  MouseSensor,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import {useTranslation} from 'react-i18next';

//MTH component import
import LogicActionControlContents from './LogicActionControlIconContents';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {getSettingLogic, putSettingLogic} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import convertLogic from '../../functions/convertLogic';
import {apiErrorAlert} from '../../functions/apiErrorAlert';

/**
 * ロジック 優先レベル入れ替え表
 * @param props props
 * @returns JSX
 */
const ChangePriorityLevelsDialog = (props: {
  /** サイトID */
  siteID: string;
  /** 編集するロジックの優先レベル */
  priorityLevels: apiIF.res.settingLogic['priority-level'][];
  /** ダイアログを表示するかどうか */
  isOpenDialog: boolean;
  /** ダイアログを表示するかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** ロジック設定を更新するかどうか（set関数） */
  setLogicInfoChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  //ロジックの設定情報
  const [logicInfo, setLogicInfo] = useState<apiIF.res.getSettingLogic['settings']>([]);
  //選択状態のロジック行
  const [activeId, setActiveId] = useState<UniqueIdentifier>();
  //ロジック設定修正API応答待ちフラグ
  const [editAPIWait, setEditAPIWait] = useState(false);

  //ロジック表の幅一覧
  const gridXsNumber = 1;
  const gridXsTarget = 3;
  const gridXsCondition = 2;
  const gridXsParameter = 2;
  const gridXsAction = 4;

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  //ロジック情報のindex番号集
  const items = useMemo(
    () => logicInfo.map((row, index) => index + 1),
    [logicInfo]
  );

  //ダイアログを開いた時、ロジック情報を取得する
  useEffect(() => {
    setEditAPIWait(true);
    //サイトのロジック設定情報取得
    getSettingLogic(props.siteID)
      .then(response => {
        setLogicInfo(response['settings']);
      })
      .finally(() => {
        setEditAPIWait(false);
      });
  }, [props.isOpenDialog, props.siteID]);

  /**
   * ロジック行ドラッグ開始時処理
   */
  const handleDragStart = (event: DragStartEvent) => {
    const {active} = event;
    const id = active.id.toString();
    setActiveId(id);
  };

  /**
   * ロジック行ドラッグ中止時処理
   */
  const handleDragCancel = () => {
    setActiveId('');
  };

  /**
   * ロジック行ドロップ時処理
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (over !== null && active.id !== over.id) {
      setLogicInfo(logic => {
        const oldIndex = items.indexOf(Number(active.id)); //ドラッグしているロジック行のNoを取得
        const newIndex = items.indexOf(Number(over.id)); //ドラッグしているロジック行と重なったロジック行のNoを取得
        return arrayMove(logic, oldIndex, newIndex); //ドラッグしているロジック行と重なったロジック行を入れ替える
      });
    }
    setActiveId('');
  };

  /**
   * OKボタン押下時処理
   */
  const handleOK = () => {
    setEditAPIWait(true);
    //APIに渡すデータ作成
    const editData: apiIF.reqParameters.putSettingLogic = {
      //並び替え後のロジック設定情報を基に作成する
      settings: logicInfo.map((logic, index) => {
        const tempLogic: apiIF.res.settingLogic = {
          ...logic,
          'priority-level': props.priorityLevels[index],
        };
        //ロジック設定をロジック編集APIに渡す形に変換する
        const editLogic = convertLogic(tempLogic);
        return editLogic;
      }),
    };

    //ロジック編集API
    putSettingLogic(props.siteID, editData)
      .then(response => {
        //ロジック設定更新フラグtrue
        props.setLogicInfoChange(true);
        handleClose();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setEditAPIWait(false);
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  type Props = {
    /** ロジック設定情報 */
    logic: apiIF.res.settingLogic;
    /** ロジック行のインデックス */
    id: number;
  };
  /**
   * ドラッグ可能ロジック行を作成
   * @param param0 param0
   * @returns JSX.Element
   */
  const DraggableRow = ({logic, id}: Props) => {
    const {attributes, listeners, setNodeRef, transform, transition} =
      useSortable({id});
    const style = {
      transform: CSS.Transform.toString(transform),
      transition: transition,
    };

    return (
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="logic__dialog__sortTable__body__row">
        <LogicBody logic={logic} id={id} />
      </Box>
    );
  };

  /**
   * 選択ロジック行を作成
   * @param param0 param0
   * @returns JSX.Element
   */
  const StaticRow = ({
    id,
  }: {
    /** 選択状態のロジック行 */
    id: UniqueIdentifier;
  }) => {
    const logic = logicInfo[Number(id) - 1];
    return (
      <Box className="logic__dialog__sortTable__body__row--staticRow">
        <LogicBody logic={logic} id={Number(id)} />
      </Box>
    );
  };

  /**
   * ロジック表示内容
   * @param param0 param0
   * @returns JSX.Element
   */
  const LogicBody = ({
    /** ロジック設定情報 */
    logic,
    /** ロジック行のインデックス */
    id,
  }: Props) => {
    /** 翻訳後の対象の名前 */
    let targetName = '';
    switch (logic.scope) {
      case 'site':
        targetName = trans('LogicSortTableDialog.list.body.target.allUnit');
        break;
      case 'floor':
        if (Number(logic['target-name']) >= 0) {
          //prettier-ignore
          targetName = trans(`LogicSortTableDialog.list.body.target.floor${logic['target-name']}`);
        } else if (Number(logic['target-name']) >= -4) {
          //prettier-ignore
          targetName = trans(`LogicSortTableDialog.list.body.target.basement${logic['target-name'].replace('-', '')}`);
        }
        break;
      case 'group':
      case 'sl': //fall through
        targetName = logic['target-name'];
        break;
    }

    return (
      <Grid container alignItems="center">
        <Grid
          item
          xs={gridXsNumber}
          className="logic__dialog__sortTable__body__row__data">
          {id}
        </Grid>
        <Grid
          item
          xs={gridXsTarget}
          className="logic__dialog__sortTable__body__row__data">
          <Box className="logic__dialog__sortTable__body__row__data__target">
            {targetName}
          </Box>
        </Grid>
        <Grid
          item
          xs={gridXsCondition}
          className="logic__dialog__sortTable__body__row__data">
          {logic['trigger'] === 'outdoor-temperature'
            ? `${trans('LogicSortTableDialog.list.body.condition.outdoorTemp')}`
            : `${trans('LogicSortTableDialog.list.body.condition.roomTemp')}`}
        </Grid>
        <Grid
          item
          xs={gridXsParameter}
          className="logic__dialog__sortTable__body__row__data">
          {`${logic['threshold']}${trans(
            'LogicSortTableDialog.list.body.parameter.temp'
          )} ${logic['range'].replace('-', ' ')}`}
        </Grid>
        <Grid
          item
          xs={gridXsAction}
          className="logic__dialog__sortTable__body__row__data">
          {/* 制御 or 通知を表示 */}
          {'notify-authority' in logic['action'] ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2}>
                <Box className="logic__dialog__sortTable__body__row__action__mailIcon">
                  <MailOutlineOutlinedIcon className="logic__dialog__sortTable__body__row__action__mailIcon__image" />
                </Box>
              </Grid>
              <Grid
                item
                xs={5}
                className="logic__dialog__sortTable__body__row__action__authority">
                {logic['action']['notify-authority']}
              </Grid>
            </Grid>
          ) : (
            <LogicActionControlContents logicInfo={logic} />
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog open={props.isOpenDialog} className="logic__dialog" maxWidth="md">
      <DialogTitle>{trans('LogicSortTableDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack component={Paper} className="logic__dialog__sortTable">
          <Grid container>
            <Grid
              item
              xs={gridXsNumber}
              className="logic__dialog__sortTable__header">
              {trans('LogicSortTableDialog.list.header.number')}
            </Grid>
            <Grid
              item
              xs={gridXsTarget}
              className="logic__dialog__sortTable__header">
              {trans('LogicSortTableDialog.list.header.target')}
            </Grid>
            <Grid
              item
              xs={gridXsCondition}
              className="logic__dialog__sortTable__header">
              {trans('LogicSortTableDialog.list.header.condition')}
            </Grid>
            <Grid
              item
              xs={gridXsParameter}
              className="logic__dialog__sortTable__header">
              {trans('LogicSortTableDialog.list.header.parameter')}
            </Grid>
            <Grid
              item
              xs={gridXsAction}
              className="logic__dialog__sortTable__header">
              {trans('LogicSortTableDialog.list.header.action')}
            </Grid>
          </Grid>
          <Box className="logic__dialog__sortTable__body">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              onDragStart={handleDragStart}
              modifiers={[
                restrictToVerticalAxis,
                restrictToWindowEdges,
                restrictToParentElement,
              ]}
              autoScroll={true}>
              <SortableContext
                items={items}
                strategy={verticalListSortingStrategy}>
                {logicInfo
                  .filter(logic => {
                    return (
                      logic['control'] === 'active' ||
                      logic['control'] === 'inactive'
                    );
                  })
                  .map((logic, index) => (
                    <DraggableRow
                      key={logic['priority-level']}
                      id={index + 1}
                      logic={logic}
                    />
                  ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeId ? <StaticRow id={activeId} /> : null}
              </DragOverlay>
            </DndContext>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          disabled={editAPIWait}
          onClick={handleClose}
          buttonText={trans('LogicSortTableDialog.buttonTxt.cancel')}
        />
        <PrimaryButton
          onClick={handleOK}
          buttonText={trans('LogicSortTableDialog.buttonTxt.ok')}
          disabled={editAPIWait}
          apiWait={editAPIWait}
        />
      </DialogActions>
    </Dialog>
  );
};
export default ChangePriorityLevelsDialog;
